import { useMemo } from 'react';
import {
  useAuthCompanyRemoveUserMutation,
  useAuthInvitationAcceptMutation,
  useAuthInvitationCreateMutation,
  useAuthInvitationDeactivateMutation,
  useAuthInvitationSendMutation,
  UserInvitationType,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import GraphQLApiUtils from '@shared/api/graphql/utils';

import { InvitationResultType, UseInvitationType } from './types';

const useInvitation = (): UseInvitationType => {
  const [createMutationRequest, createMutationResult] = useAuthInvitationCreateMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const [sendMutationRequest, sendMutationResult] = useAuthInvitationSendMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const [deactivateMutationRequest, deactivateMutationResult] = useAuthInvitationDeactivateMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const [acceptMutationRequest, acceptMutationResult] = useAuthInvitationAcceptMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const [deleteMutationRequest, deleteMutationResult] = useAuthCompanyRemoveUserMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const request = {
    create: async (email: string, companyId: string) => {
      const res = await createMutationRequest({
        variables: {
          input: { email, companyId },
        },
      });
      return res.data?.invitationCreate?.invitation as UserInvitationType;
    },
    send: async (invitationId: string, returnUrl: string) => {
      const res = await sendMutationRequest({
        variables: {
          input: { invitationId, returnUrl },
        },
      });
      return res.data?.invitationSend?.invitation as UserInvitationType;
    },
    deactivate: async (invitationId: string) => {
      const res = await deactivateMutationRequest({ variables: { input: { invitationId } } });
      return res.data?.invitationDeactivate?.invitation as UserInvitationType;
    },
    accept: async (token: string, phone: string, password: string) => {
      const res = await acceptMutationRequest({
        variables: {
          input: { token, phone, password },
        },
      });
      return !!res.data?.invitationAccept?.ok;
    },
    delete: async (userId: string, companyId: string) => {
      const res = await deleteMutationRequest({
        variables: {
          input: { userId, companyId },
        },
      });

      return !!res.data?.companyRemoveUser?.ok;
    },
  };

  const createData = useMemo(
    () => ({
      ok: createMutationResult.data?.invitationCreate?.ok!!,
      invitation: createMutationResult.data?.invitationCreate?.invitation as UserInvitationType,
      errors: GraphQLApiUtils.errorsArrayToObject(createMutationResult.data?.invitationCreate?.errors),
    }),
    [createMutationResult.data?.invitationCreate]
  );

  const sendData = useMemo(
    () => ({
      ok: sendMutationResult.data?.invitationSend?.ok!!,
      invitation: sendMutationResult.data?.invitationSend?.invitation as UserInvitationType,
    }),
    [sendMutationResult.data?.invitationSend]
  );

  const deactivateData = useMemo(
    () => ({
      ok: deactivateMutationResult.data?.invitationDeactivate?.ok!!,
      invitation: deactivateMutationResult.data?.invitationDeactivate?.invitation as UserInvitationType,
    }),
    [deactivateMutationResult.data?.invitationDeactivate]
  );

  const acceptData = useMemo(
    () => ({
      ok: acceptMutationResult.data?.invitationAccept?.ok!!,
      errors: GraphQLApiUtils.errorsArrayToObject(acceptMutationResult.data?.invitationAccept?.errors),
    }),
    [acceptMutationResult.data?.invitationAccept]
  );

  const deleteData = useMemo(
    () => ({
      ok: deleteMutationResult.data?.companyRemoveUser?.ok!!,
      errors: GraphQLApiUtils.errorsArrayToObject(deleteMutationResult.data?.companyRemoveUser?.errors),
    }),
    [deleteMutationResult.data?.companyRemoveUser]
  );

  const data = useMemo(
    () => ({
      create: createData,
      send: sendData,
      deactivate: deactivateData,
      accept: acceptData,
      delete: deleteData,
    }),
    [createData, sendData, deactivateData, acceptData, deleteData]
  );

  const result: InvitationResultType = {
    error:
      createMutationResult.error ||
      sendMutationResult.error ||
      deactivateMutationResult.error ||
      acceptMutationResult.error ||
      deleteMutationResult.error,
    loading:
      createMutationResult.loading ||
      sendMutationResult.loading ||
      deactivateMutationResult.loading ||
      acceptMutationResult.loading ||
      deleteMutationResult.loading,
    called:
      createMutationResult.called ||
      sendMutationResult.called ||
      deactivateMutationResult.called ||
      acceptMutationResult.called ||
      deleteMutationResult.called,
    data,
  };

  return [request, result];
};

export default useInvitation;
